import { createElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBanners } from '../context/BannerContext';
import { useUserProfile } from '../context/UserProfileContext';
import { useSetupChecklist } from './useSetupChecklist';
import { Settings } from 'lucide-react';

const BANNER_ID = 'setup-incomplete';
const DISMISS_KEY = 'iomi_setup_banner_dismissed';

/**
 * Registers (or removes) the "Setup Incomplete" banner for org admins
 * whose org has outstanding setup checklist items.
 */
export function useSetupBanner() {
  const { activeOrg, adminOrg } = useUserProfile();
  const orgId = activeOrg?.orgId;
  const { checklist, completedCount, totalCount, isComplete } = useSetupChecklist(
    adminOrg ? orgId : undefined,
  );
  const { addBanner, removeBanner } = useBanners();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    const val = sessionStorage.getItem(DISMISS_KEY);
    return val === 'true';
  });

  const isAdmin = !!adminOrg;

  useEffect(() => {
    // Don't show if not admin, or already complete, or dismissed this session
    if (!isAdmin || !checklist || isComplete || dismissed) {
      removeBanner(BANNER_ID);
      return;
    }

    // Check skippedAt — re-show after 7 days
    if (checklist.skippedAt) {
      const skippedDate = new Date(checklist.skippedAt);
      const daysSinceSkip = (Date.now() - skippedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceSkip < 7) {
        removeBanner(BANNER_ID);
        return;
      }
    }

    addBanner({
      id: BANNER_ID,
      priority: 5, // Show before subscription banner
      content: createElement(SetupBanner, {
        completedCount,
        totalCount,
        onComplete: () => navigate('/setup'),
        onDismiss: () => {
          setDismissed(true);
          sessionStorage.setItem(DISMISS_KEY, 'true');
          removeBanner(BANNER_ID);
        },
      }),
    });

    return () => removeBanner(BANNER_ID);
  }, [isAdmin, checklist, isComplete, dismissed, completedCount, totalCount, addBanner, removeBanner, navigate]);
}

/** Compact setup-incomplete banner. */
function SetupBanner({ completedCount, totalCount, onComplete, onDismiss }: {
  completedCount: number;
  totalCount: number;
  onComplete: () => void;
  onDismiss: () => void;
}) {
  return createElement(
    'div',
    {
      className:
        'flex items-center gap-3 px-4 py-2.5 bg-plum/10 border-b border-plum/20',
    },
    createElement(Settings, { size: 16, className: 'text-plum shrink-0' }),
    createElement(
      'p',
      { className: 'flex-1 text-xs font-medium text-midnight' },
      `Your org setup is incomplete: ${completedCount} of ${totalCount} required sections done.`,
    ),
    createElement(
      'button',
      {
        onClick: onComplete,
        className:
          'px-3 py-1.5 text-xs font-semibold text-white bg-plum hover:bg-rose rounded-lg transition-colors whitespace-nowrap',
      },
      'Complete Setup →',
    ),
    createElement(
      'button',
      {
        onClick: onDismiss,
        className: 'p-1 text-slate hover:text-midnight transition-colors',
        'aria-label': 'Dismiss',
      },
      '×',
    ),
  );
}
